export type UserRole = "super_admin" | "company_admin" | "ca" | "accountant";

export interface Permission {
  key: string;
  label: string;
}

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  ca: "Chartered Accountant",
  accountant: "Accountant",
};

export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  super_admin: [
    "platform.manage",
    "companies.view_all",
    "companies.approve",
    "subscriptions.manage",
    "analytics.platform",
    "settings.system",
    "users.manage_all",
  ],
  company_admin: [
    "company.create",
    "company.manage",
    "users.add_ca",
    "users.add_accountant",
    "users.manage_company",
    "reports.view_all",
    "invoices.manage",
    "inventory.manage",
    "gst.settings",
    "ledgers.view",
    "journals.view",
    "sales.view",
    "purchase.view",
    "eway.view",
    "audit.view",
  ],
  ca: [
    "accounting.compliance",
    "entries.review",
    "entries.approve",
    "gst.file_returns",
    "gst.ledgers",
    "reports.create",
    "tax.calculations",
    "ledgers.manage",
    "journals.manage",
    "sales.view",
    "purchase.view",
    "invoices.view",
    "inventory.view",
    "audit.view",
  ],
  accountant: [
    "entries.create",
    "sales.create",
    "purchase.create",
    "inventory.manage",
    "bills.upload",
    "expenses.record",
    "payments.record",
    "invoices.create",
    "ledgers.view",
    "journals.create",
  ],
};

export function hasPermission(role: UserRole, permission: string): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function hasAnyPermission(role: UserRole, permissions: string[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

// Sidebar navigation items per role
export interface NavItem {
  id?: string;
  title: string;
  url: string;
  icon: string;
  badge?: string;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export const ROLE_NAV_CONFIG: Record<UserRole, NavGroup[]> = {
  super_admin: [
    {
      id: "OVERVIEW",
      label: "Overview",
      items: [
        { title: "Dashboard", url: "/dashboard", icon: "LayoutDashboard" },
        { title: "Platform Analytics", url: "/dashboard/analytics", icon: "BarChart3" },
      ],
    },
    {
      id: "PLATFORM",
      label: "Platform Management",
      items: [
        { title: "Companies", url: "/dashboard/companies", icon: "Building2" },
        { title: "Users", url: "/dashboard/users", icon: "Users" },
        { title: "Subscriptions", url: "/dashboard/subscription", icon: "CreditCard" },
        { title: "Commissions", url: "/dashboard/commissions", icon: "IndianRupee" },
        { title: "Feedback", url: "/dashboard/feedback", icon: "MessageSquarePlus" },
      ],
    },
    {
      id: "VERIFICATION",
      label: "Verification & Audit",
      items: [
        { title: "CA Verification", url: "/dashboard/ca-verification", icon: "ShieldCheck" },
        { title: "Audit Logs", url: "/dashboard/audit-logs", icon: "ClipboardList" },
        { title: "Documents", url: "/dashboard/documents", icon: "FolderOpen" },
        { title: "System Test", url: "/dashboard/system-test", icon: "Activity" },
      ],
    },
    {
      id: "COMMUNICATIONS",
      label: "Communications",
      items: [
        { title: "Announcements", url: "/dashboard/announcements", icon: "Megaphone" },
        { title: "Feature Requests", url: "/dashboard/feature-requests", icon: "Lightbulb" },
      ],
    },
    {
      id: "ADMINISTRATION",
      label: "Administration",
      items: [
        { title: "Settings", url: "/dashboard/settings", icon: "Settings" },
        { title: "Profile", url: "/dashboard/profile", icon: "User" },
      ],
    },
  ],

  company_admin: [
    {
      id: "OVERVIEW",
      label: "Overview",
      items: [
        { title: "Dashboard", url: "/dashboard", icon: "LayoutDashboard" },
        { title: "Business Activity", url: "/dashboard/analytics", icon: "Activity" },
      ],
    },
    {
      id: "ACCOUNTING",
      label: "Accounting",
      items: [
        { title: "Sales", url: "/dashboard/sales", icon: "Receipt" },
        { title: "Purchase", url: "/dashboard/purchase", icon: "Calculator" },
        { title: "Invoices", url: "/dashboard/invoices", icon: "FileText" },
        { title: "Credit Notes", url: "/dashboard/credit-notes", icon: "FileText" },
        { title: "Debit Notes", url: "/dashboard/debit-notes", icon: "FileText" },
        { title: "Journals", url: "/dashboard/journals", icon: "ClipboardList" },
        { title: "Ledgers", url: "/dashboard/ledgers", icon: "BookOpen" },
      ],
    },
    {
      id: "BANKING",
      label: "Banking",
      items: [
        { title: "Bank Sync", url: "/dashboard/bank-sync", icon: "Building2" },
        { title: "Reconciliation", url: "/dashboard/reconciliation", icon: "Landmark" },
        { title: "UPI Reconciliation", url: "/dashboard/upi-reconciliation", icon: "Smartphone" },
        { title: "Payment Tracking", url: "/dashboard/payment-tracking", icon: "BarChart3" },
        { title: "Payment Gateways", url: "/dashboard/payment-gateways", icon: "CreditCard" },
        { title: "Cash Flow", url: "/dashboard/cash-flow", icon: "Activity" },
      ],
    },
    {
      id: "EXPENSES_PAYROLL",
      label: "Expenses & Payroll",
      items: [
        { title: "Expenses", url: "/dashboard/expenses", icon: "Wallet" },
        { title: "Payroll", url: "/dashboard/payroll", icon: "BadgeIndianRupee" },
        { title: "Recurring Expenses", url: "/dashboard/automation", icon: "Zap" },
      ],
    },
    {
      id: "PRODUCTS_INVENTORY",
      label: "Products & Inventory",
      items: [
        { title: "Inventory", url: "/dashboard/inventory", icon: "Package" },
        { title: "Branch Inventory", url: "/dashboard/branch-inventory", icon: "Package" },
        { title: "Stock Adjustments", url: "/dashboard/stock-adjustments", icon: "ArrowRightLeft" },
      ],
    },
    {
      id: "CONTACTS",
      label: "Contacts",
      items: [
        { title: "Customers & Suppliers", url: "/dashboard/parties", icon: "Users" },
      ],
    },
    {
      id: "GST_COMPLIANCE",
      label: "GST & Compliance",
      items: [
        { title: "GST", url: "/dashboard/gst", icon: "ShieldCheck" },
        { title: "GST Connect", url: "/dashboard/gst-connect", icon: "Link2" },
        { title: "E-Invoice", url: "/dashboard/e-invoice", icon: "QrCode" },
        { title: "GST Reports", url: "/dashboard/gst-reports", icon: "BarChart3" },
        { title: "GST Reconciliation", url: "/dashboard/gst-reconciliation", icon: "GitCompare" },
        { title: "E-Way Bills", url: "/dashboard/eway-bills", icon: "Truck" },
        { title: "Reports", url: "/dashboard/reports", icon: "BarChart3" },
        { title: "Audit Logs", url: "/dashboard/audit-logs", icon: "ClipboardList" },
      ],
    },
    {
      id: "AI_INTELLIGENCE",
      label: "AI & Intelligence",
      items: [
        { title: "AccouPro AI", url: "/dashboard/ai-assistant", icon: "Bot" },
        { title: "AI Insights", url: "/dashboard/insights", icon: "Brain" },
        { title: "AI Auto Entry", url: "/dashboard/ai-auto-entry", icon: "Zap" },
        { title: "AI Tax Advisor", url: "/dashboard/ai-tax-advisor", icon: "Landmark" },
        { title: "AI Fraud Scan", url: "/dashboard/ai-fraud-detection", icon: "Shield" },
        { title: "AI Reports", url: "/dashboard/ai-reports", icon: "FileText" },
        { title: "AI Smart Search", url: "/dashboard/ai-search", icon: "Search" },
        { title: "Voice Entry", url: "/dashboard/voice-accounting", icon: "Mic" },
      ],
    },
    {
      id: "BUSINESS_SERVICES",
      label: "Business Services",
      items: [
        { title: "Services Market", url: "/dashboard/marketplace-services", icon: "ShoppingBag" },
        { title: "Lead Generation", url: "/dashboard/leads", icon: "Target" },
        { title: "CA Access", url: "/dashboard/ca-access-approvals", icon: "ShieldCheck" },
        { title: "CA Marketplace", url: "/dashboard/ca-marketplace", icon: "Briefcase" },
        { title: "CA Packages", url: "/dashboard/ca-packages", icon: "Package" },
        { title: "Service Requests", url: "/dashboard/service-requests", icon: "FileText" },
        { title: "Education Hub", url: "/dashboard/education", icon: "GraduationCap" },
        { title: "Community Forum", url: "/dashboard/community", icon: "MessageSquare" },
        { title: "Email Center", url: "/dashboard/email-notifications", icon: "Mail" },
        { title: "Announcements", url: "/dashboard/announcements", icon: "Megaphone" },
        { title: "Feature Requests", url: "/dashboard/feature-requests", icon: "Lightbulb" },
        { title: "Discover CAs", url: "/dashboard/ca-discovery", icon: "Sparkles" },
        { title: "Referrals", url: "/dashboard/referrals", icon: "Gift" },
        { title: "Affiliate", url: "/dashboard/affiliate", icon: "Award" },
        { title: "Transactions", url: "/dashboard/marketplace-transactions", icon: "Receipt" },
      ],
    },
    {
      id: "ADMINISTRATION",
      label: "Administration",
      items: [
        { title: "Branches", url: "/dashboard/branches", icon: "Building2" },
        { title: "Branch Permissions", url: "/dashboard/branch-permissions", icon: "ShieldCheck" },
        { title: "Inter-Branch A/c", url: "/dashboard/inter-branch", icon: "ArrowRightLeft" },
        { title: "Onboarding", url: "/dashboard/onboarding", icon: "Rocket" },
        { title: "Users", url: "/dashboard/users", icon: "Users" },
        { title: "Documents", url: "/dashboard/documents", icon: "FolderOpen" },
        { title: "Import Data", url: "/dashboard/import", icon: "Upload" },
        { title: "Export Data", url: "/dashboard/export", icon: "Download" },
        { title: "Automation", url: "/dashboard/automation", icon: "Zap" },
        { title: "Settings", url: "/dashboard/settings", icon: "Settings" },
        { title: "Security", url: "/dashboard/security", icon: "ShieldCheck" },
        { title: "Backups", url: "/dashboard/backups", icon: "Database" },
        { title: "White Label", url: "/dashboard/white-label", icon: "Paintbrush" },
        { title: "Integrations", url: "/dashboard/integrations", icon: "Plug" },
        { title: "Subscription", url: "/dashboard/subscription", icon: "CreditCard" },
        { title: "Notifications", url: "/dashboard/notifications", icon: "Bell" },
        { title: "API & Mobile", url: "/dashboard/api-developer", icon: "Code" },
        { title: "Desktop App", url: "/dashboard/desktop-app", icon: "Monitor" },
        { title: "Sync Dashboard", url: "/dashboard/sync", icon: "RefreshCw" },
        { title: "Profile", url: "/dashboard/profile", icon: "User" },
      ],
    },
  ],

  ca: [
    {
      id: "OVERVIEW",
      label: "Overview",
      items: [
        { title: "Dashboard", url: "/dashboard", icon: "LayoutDashboard" },
        { title: "Business Activity", url: "/dashboard/analytics", icon: "Activity" },
      ],
    },
    {
      id: "ACCOUNTING",
      label: "Accounting",
      items: [
        { title: "Sales", url: "/dashboard/sales", icon: "Receipt" },
        { title: "Purchase", url: "/dashboard/purchase", icon: "Calculator" },
        { title: "Invoices", url: "/dashboard/invoices", icon: "FileText" },
        { title: "Credit Notes", url: "/dashboard/credit-notes", icon: "FileText" },
        { title: "Debit Notes", url: "/dashboard/debit-notes", icon: "FileText" },
        { title: "Journals", url: "/dashboard/journals", icon: "ClipboardList" },
        { title: "Ledgers", url: "/dashboard/ledgers", icon: "BookOpen" },
      ],
    },
    {
      id: "BANKING",
      label: "Banking",
      items: [
        { title: "Bank Sync", url: "/dashboard/bank-sync", icon: "Building2" },
        { title: "Reconciliation", url: "/dashboard/reconciliation", icon: "Landmark" },
        { title: "UPI Reconciliation", url: "/dashboard/upi-reconciliation", icon: "Smartphone" },
        { title: "Payment Tracking", url: "/dashboard/payment-tracking", icon: "BarChart3" },
        { title: "Payment Gateways", url: "/dashboard/payment-gateways", icon: "CreditCard" },
        { title: "Cash Flow", url: "/dashboard/cash-flow", icon: "Activity" },
      ],
    },
    {
      id: "EXPENSES_PAYROLL",
      label: "Expenses & Payroll",
      items: [
        { title: "Expenses", url: "/dashboard/expenses", icon: "Wallet" },
        { title: "Payroll", url: "/dashboard/payroll", icon: "BadgeIndianRupee" },
      ],
    },
    {
      id: "PRODUCTS_INVENTORY",
      label: "Products & Inventory",
      items: [
        { title: "Inventory", url: "/dashboard/inventory", icon: "Package" },
        { title: "Stock Adjustments", url: "/dashboard/stock-adjustments", icon: "ArrowRightLeft" },
      ],
    },
    {
      id: "CONTACTS",
      label: "Contacts",
      items: [
        { title: "Customers & Suppliers", url: "/dashboard/parties", icon: "Users" },
        { title: "Client Companies", url: "/dashboard/ca-clients", icon: "Building2" },
      ],
    },
    {
      id: "GST_COMPLIANCE",
      label: "GST & Compliance",
      items: [
        { title: "GST", url: "/dashboard/gst", icon: "ShieldCheck" },
        { title: "GST Connect", url: "/dashboard/gst-connect", icon: "Link2" },
        { title: "E-Invoice", url: "/dashboard/e-invoice", icon: "QrCode" },
        { title: "GST Reconciliation", url: "/dashboard/gst-reconciliation", icon: "GitCompare" },
        { title: "Reports", url: "/dashboard/reports", icon: "BarChart3" },
        { title: "Audit Logs", url: "/dashboard/audit-logs", icon: "ClipboardList" },
      ],
    },
    {
      id: "AI_INTELLIGENCE",
      label: "AI & Intelligence",
      items: [
        { title: "AccouPro AI", url: "/dashboard/ai-assistant", icon: "Bot" },
        { title: "AI Insights", url: "/dashboard/insights", icon: "Brain" },
        { title: "AI Auto Entry", url: "/dashboard/ai-auto-entry", icon: "Zap" },
        { title: "AI Tax Advisor", url: "/dashboard/ai-tax-advisor", icon: "Landmark" },
        { title: "AI Fraud Scan", url: "/dashboard/ai-fraud-detection", icon: "Shield" },
        { title: "AI Reports", url: "/dashboard/ai-reports", icon: "FileText" },
      ],
    },
    {
      id: "BUSINESS_SERVICES",
      label: "Business Services",
      items: [
        { title: "CA Marketplace", url: "/dashboard/ca-marketplace", icon: "Briefcase" },
        { title: "Service Requests", url: "/dashboard/service-requests", icon: "FileText" },
        { title: "My Packages", url: "/dashboard/ca-packages", icon: "Package" },
        { title: "Transactions", url: "/dashboard/marketplace-transactions", icon: "Receipt" },
        { title: "Services Market", url: "/dashboard/marketplace-services", icon: "ShoppingBag" },
        { title: "Lead Generation", url: "/dashboard/leads", icon: "Target" },
        { title: "Partner Program", url: "/dashboard/ca-partner", icon: "Award" },
        { title: "Education Hub", url: "/dashboard/education", icon: "GraduationCap" },
        { title: "Community Forum", url: "/dashboard/community", icon: "MessageSquare" },
        { title: "Announcements", url: "/dashboard/announcements", icon: "Megaphone" },
        { title: "Feature Requests", url: "/dashboard/feature-requests", icon: "Lightbulb" },
        { title: "Feedback", url: "/dashboard/feedback", icon: "MessageSquarePlus" },
      ],
    },
    {
      id: "ADMINISTRATION",
      label: "Administration",
      items: [
        { title: "Documents", url: "/dashboard/documents", icon: "FolderOpen" },
        { title: "Import Data", url: "/dashboard/import", icon: "Upload" },
        { title: "Export Data", url: "/dashboard/export", icon: "Download" },
        { title: "Automation", url: "/dashboard/automation", icon: "Zap" },
        { title: "My CA Profile", url: "/dashboard/ca-profile", icon: "User" },
        { title: "Settings", url: "/dashboard/settings", icon: "Settings" },
        { title: "Profile", url: "/dashboard/profile", icon: "User" },
      ],
    },
  ],

  accountant: [
    {
      id: "OVERVIEW",
      label: "Overview",
      items: [
        { title: "Dashboard", url: "/dashboard", icon: "LayoutDashboard" },
      ],
    },
    {
      id: "ACCOUNTING",
      label: "Accounting",
      items: [
        { title: "Sales", url: "/dashboard/sales", icon: "Receipt" },
        { title: "Purchase", url: "/dashboard/purchase", icon: "Calculator" },
        { title: "Invoices", url: "/dashboard/invoices", icon: "FileText" },
        { title: "Credit Notes", url: "/dashboard/credit-notes", icon: "FileText" },
        { title: "Debit Notes", url: "/dashboard/debit-notes", icon: "FileText" },
        { title: "Journals", url: "/dashboard/journals", icon: "ClipboardList" },
        { title: "Ledgers", url: "/dashboard/ledgers", icon: "BookOpen" },
      ],
    },
    {
      id: "BANKING",
      label: "Banking",
      items: [
        { title: "Bank Sync", url: "/dashboard/bank-sync", icon: "Building2" },
        { title: "Reconciliation", url: "/dashboard/reconciliation", icon: "Landmark" },
        { title: "UPI Reconciliation", url: "/dashboard/upi-reconciliation", icon: "Smartphone" },
        { title: "Payment Tracking", url: "/dashboard/payment-tracking", icon: "BarChart3" },
        { title: "Payment Gateways", url: "/dashboard/payment-gateways", icon: "CreditCard" },
        { title: "Cash Flow", url: "/dashboard/cash-flow", icon: "Activity" },
      ],
    },
    {
      id: "EXPENSES_PAYROLL",
      label: "Expenses & Payroll",
      items: [
        { title: "Expenses", url: "/dashboard/expenses", icon: "Wallet" },
      ],
    },
    {
      id: "PRODUCTS_INVENTORY",
      label: "Products & Inventory",
      items: [
        { title: "Inventory", url: "/dashboard/inventory", icon: "Package" },
        { title: "Stock Adjustments", url: "/dashboard/stock-adjustments", icon: "ArrowRightLeft" },
      ],
    },
    {
      id: "CONTACTS",
      label: "Contacts",
      items: [
        { title: "Customers & Suppliers", url: "/dashboard/parties", icon: "Users" },
      ],
    },
    {
      id: "AI_INTELLIGENCE",
      label: "AI & Intelligence",
      items: [
        { title: "AccouPro AI", url: "/dashboard/ai-assistant", icon: "Bot" },
        { title: "AI Insights", url: "/dashboard/insights", icon: "Brain" },
        { title: "AI Auto Entry", url: "/dashboard/ai-auto-entry", icon: "Zap" },
        { title: "AI Tax Advisor", url: "/dashboard/ai-tax-advisor", icon: "Landmark" },
        { title: "AI Reports", url: "/dashboard/ai-reports", icon: "FileText" },
      ],
    },
    {
      id: "BUSINESS_SERVICES",
      label: "Business Services",
      items: [
        { title: "Discover CAs", url: "/dashboard/ca-discovery", icon: "Sparkles" },
        { title: "CA Marketplace", url: "/dashboard/ca-marketplace", icon: "Briefcase" },
        { title: "Service Requests", url: "/dashboard/service-requests", icon: "FileText" },
        { title: "CA Packages", url: "/dashboard/ca-packages", icon: "Package" },
        { title: "Services Market", url: "/dashboard/marketplace-services", icon: "ShoppingBag" },
        { title: "Education Hub", url: "/dashboard/education", icon: "GraduationCap" },
        { title: "Community Forum", url: "/dashboard/community", icon: "MessageSquare" },
        { title: "Announcements", url: "/dashboard/announcements", icon: "Megaphone" },
        { title: "Feature Requests", url: "/dashboard/feature-requests", icon: "Lightbulb" },
        { title: "Feedback", url: "/dashboard/feedback", icon: "MessageSquarePlus" },
      ],
    },
    {
      id: "ADMINISTRATION",
      label: "Administration",
      items: [
        { title: "Documents", url: "/dashboard/documents", icon: "FolderOpen" },
        { title: "Settings", url: "/dashboard/settings", icon: "Settings" },
        { title: "Profile", url: "/dashboard/profile", icon: "User" },
      ],
    },
  ],
};
